import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { roundMoney } from '@/shared/helpers';

import type {
	BookingGlassOptionKey,
	EstimateCost,
	EstimateCostVariants,
	EstimateOption,
	EstimateRepairOption,
	EstimateResponse,
	EstimateVehicle,
	InstallTimeSpan,
	QuickQuoteSettings
} from '@/entities/quickQuote/lib/booking-api.types';
import type { LicensePlate } from '@/widgets/quickQuoteDialog/model/types';

export const MIN_REPAIR_CHIP_COUNT = 1;
export const MAX_REPAIR_CHIP_COUNT = 3;

export const clampRepairChipCount = (value: number) =>
	Math.max(MIN_REPAIR_CHIP_COUNT, Math.min(MAX_REPAIR_CHIP_COUNT, value));

const emptyCost = (): EstimateCost => ({
	materials: 0,
	labor: 0,
	materialTax: 0,
	laborTax: 0,
	total: 0
});

const addCosts = (costs: EstimateCost[]): EstimateCost => {
	const total = costs.reduce(
		(result, cost) => ({
			materials: result.materials + cost.materials,
			labor: result.labor + cost.labor,
			materialTax: result.materialTax + cost.materialTax,
			laborTax: result.laborTax + cost.laborTax,
			total: result.total + cost.total
		}),
		emptyCost()
	);

	return {
		materials: roundMoney(total.materials),
		labor: roundMoney(total.labor),
		materialTax: roundMoney(total.materialTax),
		laborTax: roundMoney(total.laborTax),
		total: roundMoney(total.total)
	};
};

export const getRepairGlassCost = (
	option: EstimateRepairOption,
	chips: number,
	isMobile: boolean
): EstimateCost => {
	const chipCount = clampRepairChipCount(chips);
	const chipOption = option.chipOptions.find(
		item => item.chipCount === chipCount
	);
	return chipOption ? getEstimateCost(chipOption, isMobile) : emptyCost();
};

export const getEstimateCost = (
	item: EstimateCostVariants,
	isMobile: boolean
): EstimateCost => {
	if (isMobile) {
		if (!item.costMobile) throw new Error('Mobile estimate cost is missing');
		return item.costMobile;
	}

	if (!item.costInShop) throw new Error('In-shop estimate cost is missing');
	return item.costInShop;
};

export const useBookingQuoteStore = defineStore('entitiesBookingQuote', () => {
	const currentStep = ref<string>();
	const estimate = ref<EstimateResponse>();
	const estimateVehicles = ref<EstimateVehicle[]>([]);
	const selectedVehicleKey = ref<string>();
	const vehiclePlate = ref<LicensePlate>();
	const selectedGlassOption = ref<BookingGlassOptionKey>('replace');
	const chipCount = ref<number>(MIN_REPAIR_CHIP_COUNT);
	const selectedExtraOptionKeys = ref<string[]>([]);

	const installTimeSpans = ref<InstallTimeSpan[]>([]);
	const excludeHolidays = ref(false);
	const excludedWeekdays = ref<number[]>([]);
	const finalRedirectUrl = ref<string | null>(null);
	const disallowForDaysAfterToday = ref(0);
	const bookingPhone = ref<string | null>(null);
	const bookingColor = ref('green');
	const paymentRequired = ref(false);
	const phoneVerificationRequired = ref(false);
	const isPriceLevelExist = ref(false);
	const isTurned = ref(false);
	const maxBlockedDate = ref<Date>();
	const prepaymentPercent = ref(100);
	const prepaymentFlat = ref(0);
	const prepaymentType = ref('percent');
	const shopName = ref('Glass');
	const radiusIsTurned = ref(false);
	const radiusInMiles = ref<number | string | null>(null);
	const shopAddress = ref<QuickQuoteSettings['address']>(null);

	const dueTodayPercent = computed(() => {
		return prepaymentType.value === 'percent'
			? `(${prepaymentPercent.value}%)`
			: '';
	});

	const resetEstimate = () => {
		estimate.value = undefined;
		estimateVehicles.value = [];
		selectedVehicleKey.value = undefined;
		vehiclePlate.value = undefined;
		selectedGlassOption.value = 'replace';
		chipCount.value = MIN_REPAIR_CHIP_COUNT;
		selectedExtraOptionKeys.value = [];
	};

	const startOver = () => {
		resetEstimate();
		currentStep.value = undefined;
	};

	const applyQuoteSettings = (settings: QuickQuoteSettings) => {
		if (settings.color) bookingColor.value = settings.color;
		installTimeSpans.value = settings.install_time_spans ?? [];
		excludeHolidays.value = Boolean(settings.exclude_holidays);
		excludedWeekdays.value = settings.excluded_weekdays ?? [];
		finalRedirectUrl.value = settings.final_redirect_url;
		bookingPhone.value = settings.booking_phone;
		phoneVerificationRequired.value = Boolean(
			settings.is_phone_verification_required
		);
		prepaymentPercent.value = settings.prepayment_percent;
		prepaymentFlat.value = settings.prepayment_flat;
		prepaymentType.value = settings.prepayment_type;
		maxBlockedDate.value = settings.maxBlockedDate;
		radiusIsTurned.value = Boolean(settings.radius_is_turned);
		radiusInMiles.value = settings.radius_in_miles ?? null;
		shopAddress.value = settings.address ?? null;
		isPriceLevelExist.value = !!settings.pricelevel || !!settings.shop_rate;
		isTurned.value = !!settings.is_turned;
		disallowForDaysAfterToday.value = Math.max(
			0,
			Math.trunc(settings.disallow_for_days_after_today)
		);
		if (settings.shop?.name) shopName.value = settings.shop.name;

		if (
			settings.payment_enabled &&
			settings.shop?.is_turned &&
			settings.shop?.stripe_agreement
		) {
			if (
				settings.prepayment_type === 'percent' &&
				settings.prepayment_percent > 0
			)
				paymentRequired.value = true;
			if (settings.prepayment_type === 'flat' && settings.prepayment_flat > 0)
				paymentRequired.value = true;
		}
	};

	const vehicles = computed(() =>
		estimate.value?.vehicles?.length
			? estimate.value.vehicles
			: estimateVehicles.value
	);
	const estimateId = computed(() => estimate.value?.estimateId);
	const vehicleSearchComplete = computed(
		() => Boolean(estimate.value) || Boolean(estimateVehicles.value.length)
	);
	const selectedVehicle = computed(() =>
		vehicles.value.find(vehicle => vehicle.key === selectedVehicleKey.value)
	);
	const vehicleVin = computed(() => selectedVehicle.value?.vin ?? undefined);
	const availableGlassOptions = computed(() => estimate.value?.optimal ?? []);
	const selectedGlass = computed(
		() =>
			availableGlassOptions.value.find(
				option => option.key === selectedGlassOption.value
			) ?? availableGlassOptions.value[0]
	);
	const requiredOptions = computed(() => estimate.value?.requiredOptions ?? []);
	const selectableExtraOptions = computed(
		() => estimate.value?.extraOptions ?? []
	);
	const requiredRecalOptions = computed(() =>
		requiredOptions.value.filter(option => option.key === 'recal')
	);
	const requiredMobileFeeOption = computed(() =>
		requiredOptions.value.find(option => option.key === 'mobileFee')
	);
	const optionalMobileFeeOption = computed(() =>
		selectableExtraOptions.value.find(option => option.key === 'mobileFee')
	);
	const selectedExtraOptions = computed(() =>
		selectableExtraOptions.value.filter(option =>
			selectedExtraOptionKeys.value.includes(option.key)
		)
	);
	const isMobileService = computed(() => {
		if (estimate.value?.mobileServiceMode === 'always') return true;
		if (estimate.value?.mobileServiceMode === 'never') return false;

		return (
			requiredOptions.value.some(option => option.key === 'mobileFee') ||
			selectedExtraOptionKeys.value.includes('mobileFee')
		);
	});
	const getOptionCost = (option: EstimateCostVariants) =>
		getEstimateCost(option, isMobileService.value);

	const glassCost = computed(() => {
		const glass = selectedGlass.value;
		if (!glass) return emptyCost();
		return glass.key === 'repair'
			? getRepairGlassCost(glass, chipCount.value, isMobileService.value)
			: getOptionCost(glass);
	});

	const mobileFeeCost = computed(() => {
		const mobileFee = [
			...requiredOptions.value,
			...selectedExtraOptions.value
		].find(option => option.key === 'mobileFee');
		return mobileFee ? getOptionCost(mobileFee) : undefined;
	});
	const selectedCosts = computed(() => {
		const glass = selectedGlass.value;
		return [
			...(glass ? [glassCost.value] : []),
			...requiredOptions.value.map(getOptionCost),
			...selectedExtraOptions.value.map(getOptionCost)
		];
	});
	const quoteCost = computed(() => addCosts(selectedCosts.value));
	const quoteTotal = computed(() =>
		selectedCosts.value.length ? quoteCost.value.total : null
	);
	const prepaymentAmountCents = computed(() => {
		if (quoteTotal.value === null) return null;
		else if (prepaymentType.value === 'percent') {
			return Math.round(
				quoteTotal.value * 100 * (prepaymentPercent.value / 100)
			);
		} else if (prepaymentType.value === 'flat') {
			const overallTotal = quoteTotal.value * 100;
			return Math.max(
				200,
				overallTotal >= prepaymentFlat.value
					? prepaymentFlat.value
					: overallTotal
			);
		} else return null;
	});
	const prepaymentAmount = computed(() =>
		prepaymentAmountCents.value === null
			? null
			: prepaymentAmountCents.value / 100
	);
	const selectedOptionKeys = computed(() => [
		...requiredOptions.value.map(option => option.key),
		...selectedExtraOptions.value.map(option => option.key)
	]);

	const selectVehicle = (vehicle: EstimateVehicle) => {
		selectedVehicleKey.value = vehicle.key;
	};

	const applyEstimate = (response: EstimateResponse) => {
		estimate.value = response;
		estimateVehicles.value = [];
		selectedVehicleKey.value =
			response.vehicles.length === 1 ? response.vehicles[0].key : undefined;
		const defaultGlass =
			response.optimal.find(option => option.key === 'replace') ??
			response.optimal[0];
		selectedGlassOption.value = defaultGlass?.key ?? 'replace';
		chipCount.value = MIN_REPAIR_CHIP_COUNT;
		selectedExtraOptionKeys.value = [];
	};

	const applyEstimateVehicles = (vehicles: EstimateVehicle[]) => {
		estimate.value = undefined;
		estimateVehicles.value = vehicles;
		selectedVehicleKey.value =
			vehicles.length === 1 ? vehicles[0].key : undefined;
		selectedGlassOption.value = 'replace';
		chipCount.value = MIN_REPAIR_CHIP_COUNT;
		selectedExtraOptionKeys.value = [];
	};

	const setSelectedGlassOption = (value: BookingGlassOptionKey) => {
		selectedGlassOption.value = value;
	};

	const setChipCount = (value: number) => {
		chipCount.value = clampRepairChipCount(value);
	};

	const toggleExtraOption = (option: EstimateOption) => {
		if (!selectableExtraOptions.value.some(item => item.key === option.key))
			return;

		if (selectedExtraOptionKeys.value.includes(option.key)) {
			selectedExtraOptionKeys.value = selectedExtraOptionKeys.value.filter(
				key => key !== option.key
			);
			return;
		}

		selectedExtraOptionKeys.value = [
			...selectedExtraOptionKeys.value,
			option.key
		];
	};

	return {
		applyEstimate,
		applyEstimateVehicles,
		applyQuoteSettings,
		availableGlassOptions,
		bookingColor,
		bookingPhone,
		chipCount,
		currentStep,
		disallowForDaysAfterToday,
		excludeHolidays,
		excludedWeekdays,
		estimateId,
		finalRedirectUrl,
		glassCost,
		maxBlockedDate,
		getOptionCost,
		installTimeSpans,
		isMobileService,
		mobileFeeCost,
		paymentRequired,
		phoneVerificationRequired,
		prepaymentAmount,
		prepaymentAmountCents,
		prepaymentPercent,
		prepaymentType,
		dueTodayPercent,
		isPriceLevelExist,
		isTurned,
		quoteCost,
		quoteTotal,
		radiusIsTurned,
		radiusInMiles,
		requiredMobileFeeOption,
		requiredOptions,
		requiredRecalOptions,
		resetEstimate,
		selectVehicle,
		selectableExtraOptions,
		optionalMobileFeeOption,
		selectedExtraOptionKeys,
		selectedExtraOptions,
		selectedGlass,
		selectedGlassOption,
		selectedOptionKeys,
		selectedVehicle,
		selectedVehicleKey,
		setChipCount,
		setSelectedGlassOption,
		shopAddress,
		shopName,
		startOver,
		toggleExtraOption,
		vehiclePlate,
		vehicleSearchComplete,
		vehicleVin,
		vehicles
	};
});
